import React, { useEffect, Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { store } from './redux/store';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { initializeAnalytics } from './services/analytics';
import { ErrorBoundary } from './ErrorBoundary';

// Only import critical components eagerly
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import AuthFlow from './components/AuthFlow';

// Use lazy loading for pages to reduce initial bundle size
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const Preferences = lazy(() => import('./pages/Preferences'));
const Satellites = lazy(() => import('./pages/Satellites'));
const Simulation = lazy(() => import('./pages/Simulation'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));
const Photos = lazy(() => import('./pages/Photos'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const PreferencesSetupPage = lazy(() => import('./pages/PreferencesSetupPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const StarlinkCommandCenterPage = lazy(() => import('./pages/StarlinkCommandCenterPage'));
const StarlinkPage = lazy(() => import('./pages/StarlinkPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentErrorHandler = lazy(() => import('./components/PaymentErrorHandler'));

// Loading component for Suspense fallback
const Loading = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '5px solid rgba(76, 29, 149, 0.3)',
      borderTop: '5px solid #4338ca',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  useEffect(() => {
    // Initialize analytics when app loads
    initializeAnalytics();
  }, []);

  return (
    <Provider store={store}>
      <HelmetProvider>
        <AuthProvider>
          <ThemeProvider>
            <Router>
              <ErrorBoundary>
                <AuthFlow>
                  <Suspense fallback={<Loading />}>
                    <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/payment-success" element={<PaymentSuccessPage />} />
                    <Route path="/payment-error" element={<PaymentErrorHandler />} />
                    <Route path="/blog" element={<BlogListPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/starlink" element={<StarlinkPage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/login" element={<AuthPage />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/error" element={<AuthPage />} />
                    <Route path="/success" element={<SuccessPage />} />
                    <Route path="/space-control" element={<Navigate to="/orbit-control-9e2f1a/login" replace />} />
                    <Route path="/orbit-control-9e2f1a/login" element={<AdminLogin />} />
                    
                    {/* Newsletter routes */}
                    <Route
                      path="/newsletter/latest"
                      element={
                        <ProtectedRoute>
                          <NewsletterPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/newsletter/archive"
                      element={
                        <ProtectedRoute>
                          <NewsletterPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/newsletter/:slug"
                      element={
                        <ProtectedRoute>
                          <NewsletterPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Protected routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/preferences"
                      element={
                        <ProtectedRoute>
                          <Preferences />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/satellites"
                      element={
                        <ProtectedRoute>
                          <Satellites />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/simulation"
                      element={
                        <ProtectedRoute>
                          <Simulation />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/photos"
                      element={
                        <ProtectedRoute>
                          <Photos />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/starlink-command"
                      element={
                        <ProtectedRoute>
                          <StarlinkCommandCenterPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/onboarding"
                      element={<OnboardingPage />}
                    />
                    <Route
                      path="/preferences-setup"
                      element={<PreferencesSetupPage />}
                    />
                    <Route
                      path="/starlink-command"
                      element={
                        <ProtectedRoute>
                          <StarlinkCommandCenterPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={<Navigate to="/orbit-control-9e2f1a" replace />}
                    />
                    <Route
                      path="/orbit-control-9e2f1a"
                      element={
                        <ProtectedRoute requireAdmin>
                          <AdminPanel />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Catch all - redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </AuthFlow>
              </ErrorBoundary>
              
              {/* Vercel Analytics components */}
              <Analytics />
              <SpeedInsights />
            </Router>
          </ThemeProvider>
        </AuthProvider>
      </HelmetProvider>
    </Provider>
  );
}

export default App;